import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Loader,
  Center,
  SimpleGrid,
  Avatar,
  Badge,
  Box,
  ThemeIcon,
  ActionIcon,
  Modal,
  TextInput,
  Alert
} from '@mantine/core';
import { friendsAPI } from '../services/api';

const FriendRequestsPage = () => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(true);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [friendEmail, setFriendEmail] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);

  useEffect(() => {
    loadFriendsData();
  }, []);

  const loadFriendsData = async () => {
    try {
      const [friendsRes, requestsRes] = await Promise.all([
        friendsAPI.getFriends(),
        friendsAPI.getRequests()
      ]);

      setFriends(friendsRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Failed to load friends data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async () => {
    if (!friendEmail.trim()) return;

    setSendingRequest(true);
    try {
      // This would need to be implemented with a search endpoint
      // For now, this is a placeholder
      await friendsAPI.sendRequest(friendEmail);
      setFriendEmail('');
      setShowAddFriend(false);
      loadFriendsData(); // Refresh data
    } catch (error) {
      console.error('Failed to send request:', error);
      // Show specific error message from backend if available
      const errorMessage = error.response?.data?.error || 'Failed to send friend request. Please try again.';
      alert(errorMessage);
    } finally {
      setSendingRequest(false);
    }
  };

  const handleRespondToRequest = async (requestId, action) => {
    try {
      await friendsAPI.respondToRequest(requestId, action);
      loadFriendsData(); // Refresh data
    } catch (error) {
      console.error('Failed to respond to request:', error);
      const errorMessage = error.response?.data?.error || `Failed to ${action} friend request.`;
      alert(errorMessage);
    }
  };

  const handleRemoveFriendship = async (friendId) => {
    try {
      await friendsAPI.removeFriendship(friendId);
      loadFriendsData(); // Refresh data
    } catch (error) {
      console.error('Failed to remove friendship:', error);
      const errorMessage = error.response?.data?.error || 'Failed to remove friendship.';
      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading your friends...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Enhanced Header */}
        <Box mb="xl" py="lg">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title
                order={1}
                size="h1"
                fw={700}
                style={{
                  background: 'linear-gradient(45deg, #1971c2 0%, #15aabf 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}
              >
                👥 Friends
              </Title>
              <Text size="xl" c="dimmed" fw={400}>
                Connect with friends to share personas and enable social gifting
              </Text>
            </Box>
            <Button
              size="lg"
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
              onClick={() => setShowAddFriend(true)}
              style={{ boxShadow: '0 4px 12px rgba(25,113,194,0.3)' }}
            >
              ➕ Add Friend
            </Button>
          </Group>
        </Box>

        {/* Add Friend Modal */}
        <Modal
          opened={showAddFriend}
          onClose={() => setShowAddFriend(false)}
          title={
            <Text size="xl" fw={700}>
              ➕ Add Friend
            </Text>
          }
          size="md"
          centered
        >
          <Stack gap="md">
            <TextInput
              label="Friend's Email"
              placeholder="Enter friend's email address"
              value={friendEmail}
              onChange={(e) => setFriendEmail(e.target.value)}
              size="md"
              required
            />

            <Group grow>
              <Button
                size="lg"
                variant="gradient"
                gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                onClick={handleSendRequest}
                loading={sendingRequest}
              >
                Send Friend Request
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setShowAddFriend(false)}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Friend Requests */}
        {(requests.received.length > 0 || requests.sent.length > 0) && (
          <Box mb="xl">
            <Title order={2} mb="lg">
              📨 Pending Requests
            </Title>

            {requests.received.length > 0 && (
              <Box mb="lg">
                <Title order={3} mb="md">
                  Requests to Accept
                </Title>
                <Stack gap="sm">
                  {requests.received.map(request => (
                    <Card
                      key={request.id}
                      p="lg"
                      radius="lg"
                      withBorder
                      style={{
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                      }}
                    >
                      <Group justify="space-between" align="center">
                        <Box>
                          <Text fw={600} size="lg">{request.name}</Text>
                          <Text c="dimmed">{request.email}</Text>
                        </Box>
                        <Group gap="xs">
                          <Button
                            color="teal"
                            onClick={() => handleRespondToRequest(request.id, 'accept')}
                          >
                            ✓ Accept
                          </Button>
                          <Button
                            color="red"
                            variant="outline"
                            onClick={() => handleRespondToRequest(request.id, 'reject')}
                          >
                            ✕ Decline
                          </Button>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}

            {requests.sent.length > 0 && (
              <Box>
                <Title order={3} mb="md">
                  Sent Requests
                </Title>
                <Stack gap="sm">
                  {requests.sent.map(request => (
                    <Card
                      key={request.id}
                      p="lg"
                      radius="lg"
                      withBorder
                      style={{
                        background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                      }}
                    >
                      <Group justify="space-between" align="center">
                        <Box>
                          <Text fw={600}>Sent to {request.name}</Text>
                          <Text c="dimmed">{request.email} - Pending response</Text>
                        </Box>
                        <Button
                          size="sm"
                          color="red"
                          variant="outline"
                          onClick={() => handleRemoveFriendship(request.id)}
                        >
                          Cancel
                        </Button>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Friends List */}
        <Box>
          <Title order={2} mb="lg">
            🤝 Your Friends ({friends.length})
          </Title>

          {friends.length === 0 ? (
            <Card
              p="xl"
              radius="lg"
              withBorder
              style={{
                background: 'linear-gradient(135deg, rgba(25,113,194,0.05) 0%, rgba(0,0,0,0) 100%)',
                borderStyle: 'dashed',
                borderWidth: '2px'
              }}
            >
              <Center py="xl">
                <Stack align="center" gap="md">
                  <ThemeIcon size={80} radius="xl" variant="light" color="blue">
                    <Text size="40px">👥</Text>
                  </ThemeIcon>
                  <Title order={3} ta="center">
                    No friends yet
                  </Title>
                  <Text c="dimmed" ta="center" maw={400}>
                    Start building your social gifting network by adding friends to share personas and collaborate on gifts
                  </Text>
                  <Button
                    size="lg"
                    variant="gradient"
                    gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                    onClick={() => setShowAddFriend(true)}
                    mt="md"
                  >
                    Add Your First Friend
                  </Button>
                </Stack>
              </Center>
            </Card>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
              {friends.map(friend => (
                <Card
                  key={friend.friendship_id}
                  p="lg"
                  radius="lg"
                  withBorder
                  shadow="sm"
                  style={{
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Group justify="space-between" mb="md">
                    <Group gap="sm">
                      <Avatar
                        size={60}
                        radius="xl"
                        variant="gradient"
                        gradient={{ from: 'blue', to: 'cyan', deg: 45 }}
                      >
                        <Text size="xl">👤</Text>
                      </Avatar>
                      <Box>
                        <Text fw={700} size="lg">
                          {friend.name}
                        </Text>
                        <Text c="dimmed" size="sm">
                          {friend.email}
                        </Text>
                      </Box>
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => handleRemoveFriendship(friend.friendship_id)}
                    >
                      ✕
                    </ActionIcon>
                  </Group>

                  <Group grow>
                    <Button
                      variant="light"
                      color="blue"
                      onClick={() => navigate(`/friends/${friend.id}/personas`)}
                    >
                      👁️ View Personas
                    </Button>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FriendRequestsPage;
